'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCentrifugo } from '@/contexts/CentrifugoContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useProfileData } from '@/queries/useProfileQueries';
import { queryKeys } from '@/lib/queryClient';
import { useCatalog } from '@/screens/AchievementsScreen/hooks/useCatalog';
import { pickTitle, useAchievementLang } from '@/screens/AchievementsScreen/utils/pickLocale';
import { useOpenAchievementSheet } from '@/stores/achievementSheetStore';
import { useOpenClaimSheet } from '@/stores/claimSheetStore';
import { AchievementUnlockToast } from '@/components/AchievementUnlockToast';
import { Toast, ToastTile, showCustomToast } from '@/components/Toast';
import { BandTickets } from '@/components/BandTickets';
import { IoCheckmarkCircle } from '@/components/icons';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import bandTranslations from '@/screens/BandScreen/BandScreen.translations';
import { QUEST_COPY, renderQuestTitle } from '@/screens/BandScreen/questCopy';
import { scrollToBandQuests } from '@/screens/BandScreen/bandQuestsAnchor';
import { loadSounds, playSfx } from '@/lib/audioEngine';
import type { CatalogReward, CatalogResponse } from '@/services/generated/Api';

const UNLOCK_SFX = 'achievement_unlock';
// Заголовок достижения или задания — это его название, до шести слов, плюс строка награды:
// 1,5 с на замечание и около 2 с на чтение. Оставлено 6 с, а не сокращено до четырёх, по
// другой причине: эта карточка ещё и кнопка (ведёт к достижению или к заданиям клуба),
// и время жизни здесь — время, за которое по ней можно успеть попасть.
const TOAST_DURATION_MS = 6000;

interface AchievementUnlockedMessage {
  type: 'achievement_unlocked';
  key?: string;
  unlockedAt?: string;
  reward?: CatalogReward;
  /** Whether the bonus was actually credited (false → unlocked but reward locked behind deposits). */
  claimed?: boolean;
  hiddenUntilUnlocked?: boolean;
}

interface BandQuestCompletedMessage {
  type: 'band_quest_completed';
  questId?: string;
  tickets?: number;
  completedAt?: string;
}

type UserNotificationMessage = AchievementUnlockedMessage | BandQuestCompletedMessage | { type?: string };

/**
 * The one subscription to `${prefix}_user#${userId}` — the private channel the backend publishes
 * every in-app notification to. Each `type` gets its own toast and its own cache invalidation;
 * unknown types are ignored, so a backend can ship a new notification before the client knows it.
 */
export const useUserNotificationsSubscription = () => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();
  const profile = useProfileData();
  const userId = profile?.id;
  const openSheet = useOpenAchievementSheet();
  const openClaimSheet = useOpenClaimSheet();
  const lang = useAchievementLang();
  const { t: tBand } = useLocalTranslation(bandTranslations);
  useCatalog();

  // Read the LIVE navigation surface inside the toast click handler (which is
  // created at unlock time and may outlive a navigation change).
  const navigation = useNavigation();
  const navRef = useRef(navigation);
  navRef.current = navigation;

  // warm-decode the unlock fanfare so it fires the instant the toast pops
  useEffect(() => { void loadSounds([UNLOCK_SFX]).catch(() => undefined); }, []);

  const handleAchievementUnlocked = useCallback(
    async (event: AchievementUnlockedMessage, client: QueryClient, currentUserId: string) => {
      const key = String(event.key ?? '');

      // Hidden rules carry '???' in the cached catalog until the unlock;
      // refetch FIRST so the title/icon are the de-redacted values.
      if (event.hiddenUntilUnlocked) {
        await client.refetchQueries({ queryKey: queryKeys.achievementsCatalog, exact: true });
      }

      const catalog = client.getQueryData<CatalogResponse>(queryKeys.achievementsCatalog);
      const item = catalog?.items.find((i) => i.key === key);
      // Same locale pick as the achievement tiles — one implementation, and it reads the
      // person's language instead of `<html lang>`, which the server stamps once and nobody
      // updates afterwards (#82). `pickTitle` yields '' where this used to yield undefined;
      // both are falsy, so a redacted secret still falls through to the trophy glyph.
      const title = pickTitle(item?.i18n, lang);
      const displayTitle = title && title !== '???' ? title : '🏆';
      const credits = event.reward?.credits ?? item?.reward?.credits ?? 0;
      const xp = event.reward?.xp ?? item?.reward?.xp ?? 0;
      const claimed = event.claimed ?? true;
      const iconFile = item?.iconFile ?? '';

      playSfx(UNLOCK_SFX, 0.6); // fanfare as the unlock toast pops

      showCustomToast(
        (id) => (
          <AchievementUnlockToast
            iconFile={iconFile}
            title={displayTitle}
            rewardCredits={credits}
            rewardXp={xp}
            claimed={claimed}
            onDismiss={() => toast.dismiss(id)}
            onClick={() => {
              // Never interrupt an in-flight case roulette.
              if (navRef.current.getCurrentScreen?.().screen === 'case-roulette') return;
              if (!claimed) {
                // Reward locked — take them to the catalogue and open the claim sheet.
                navRef.current.navigate('achievements');
                openClaimSheet();
                return;
              }
              navRef.current.changeTab('profile');
              openSheet(key);
            }}
          />
        ),
        { duration: TOAST_DURATION_MS },
      );

      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.achievementsMine }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsCatalog }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsStats(key) }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsRecentReceivers(key) }),
        client.invalidateQueries({ queryKey: queryKeys.achievementsUser(currentUserId) }),
        // Reward credits + XP land on the user's row — refresh shell widgets
        // so the toast and header counters update together.
        client.invalidateQueries({ queryKey: queryKeys.profile }),
        client.invalidateQueries({ queryKey: queryKeys.xpStatus }),
      ]);
    },
    [lang, openClaimSheet, openSheet],
  );

  const handleBandQuestCompleted = useCallback(
    async (event: BandQuestCompletedMessage, client: QueryClient) => {
      const tickets = event.tickets ?? 0;
      const title = renderQuestTitle(event.questId ? QUEST_COPY[event.questId] : undefined, tBand);

      playSfx(UNLOCK_SFX, 0.6);

      showCustomToast(
        (id) => (
          <Toast
            accent="blue"
            leading={
              <ToastTile>
                <span className="flex h-full w-full items-center justify-center text-blue-400">
                  <IoCheckmarkCircle size={28} />
                </span>
              </ToastTile>
            }
            overline={tBand('questCompletedToastOverline')}
            title={title}
            description={
              <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-blue-400">
                +<BandTickets amount={tickets} iconSize={11} />
              </span>
            }
            onDismiss={() => toast.dismiss(id)}
            onClick={() => {
              // Never interrupt an in-flight case roulette.
              if (navRef.current.getCurrentScreen?.().screen === 'case-roulette') return;
              navRef.current.navigate('band');
              scrollToBandQuests();
            }}
          />
        ),
        { duration: TOAST_DURATION_MS },
      );

      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.bandQuests }),
        client.invalidateQueries({ queryKey: queryKeys.bandStatus }),
      ]);
    },
    [tBand],
  );

  useEffect(() => {
    if (!isConnected || !userId) return;

    const channel = channelPrefix ? `${channelPrefix}_user#${userId}` : `user#${userId}`;

    const unsubscribe = subscribe(channel, (data) => {
      void (async () => {
        const event = data as UserNotificationMessage;
        switch (event?.type) {
          case 'achievement_unlocked':
            await handleAchievementUnlocked(event as AchievementUnlockedMessage, queryClient, userId);
            break;
          case 'band_quest_completed':
            await handleBandQuestCompleted(event as BandQuestCompletedMessage, queryClient);
            break;
          default:
            break;
        }
      })();
    });

    return unsubscribe;
  }, [
    isConnected,
    channelPrefix,
    userId,
    subscribe,
    queryClient,
    handleAchievementUnlocked,
    handleBandQuestCompleted,
  ]);
};
